import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { typographyStyles } from '../styles/typography';
import { formatDateForPDF } from './dataFormatters';
import { createT } from './simpleTranslate';
import { PDFSurveyRecordRenderer } from './contentRenderer';
import { PDFSmartFieldRenderer } from './PDFSmartFieldRenderer';
import { isSmartField } from '../../../utils/index.jsx';

/**
 * Unified PDF Field Renderer
 * 
 * Handles rendering of all field types in PDF documents.
 * This provides a consistent way to render fields across all PDFs.
 */

interface PDFFieldRendererProps {
  field: {
    id: string | number;
    name: string;
    label: string;
    type: string;
    order?: number;
    options?: string[] | any[];
  };
  value: any;
  locale?: string;
}

export const PDFFieldRenderer: React.FC<PDFFieldRendererProps> = ({
  field,
  value,
  locale = 'fr',
}) => {
  const t = createT(locale);

  // Handle empty/null values
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return (
      <Text style={typographyStyles.body2}>
        {isSmartField(field.type) ? t('general.no', 'Non') : '-'}
      </Text>
    );
  }

  // Handle different field types
  switch (field.type) {
    case 'textarea':
    case 'medical_history_template':
    case 'medical_history_ai':
      // HTML content - use HTML renderer
      const htmlContent = typeof value === 'string' ? value : 
        (Array.isArray(value) && value.length > 0 ? 
          value[0].medical_history_ai || value[0]['template_medical_history-notes'] || '' : '');
      
      return (
        <PDFSurveyRecordRenderer 
          recordValue={htmlContent}
          fallbackStrategy="plaintext"
        />
      );

    case 'date':
      const dateValue = typeof value === 'string' ? new Date(value) : value;
      return (
        <Text style={typographyStyles.body2}>
          {formatDateForPDF(dateValue, locale)}
        </Text>
      );

    case 'multioption':
      const options = Array.isArray(value) ? value.join(', ') : value;
      return (
        <Text style={typographyStyles.body2}>
          {options}
        </Text>
      );

    case 'file':
      return (
        <Text style={typographyStyles.body2}>
          {t('general.file_attached', 'Fichier joint')}
        </Text>
      );

    default:
      // Handle smart fields
      if (isSmartField(field.type)) {
        return (
          <PDFSmartFieldRenderer
            type={field.type}
            value={value}
            locale={locale}
          />
        );
      }
      
      // Regular text/number fields
      return (
        <Text style={typographyStyles.body2}>
          {String(value)}
        </Text>
      );
  }
};

export default PDFFieldRenderer;

